import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/contexts/auth-context"
import { useRoleAccess } from "@/hooks/useRoleAccess"
import { useParams, Link } from "react-router-dom"
import { useCredentialErrorCount } from "@/hooks/use-credential-error-count"
import {
  Settings,
  HelpCircle,
  UserCircle,
  Sparkles,
  LogOut,
  Plug,
  GitBranch,
  Activity,
  Heart,
  TrendingUp,
  GitCompareArrows,
  Target,
  Layers,
  Shield,
  MessageSquare,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface SidebarProps {
  collapsed: boolean
  onNavigate?: (path: string) => void
  currentPath?: string
}

// Helper component for org-specific navigation items
function OrgNavItem({
  path,
  icon: Icon,
  label,
  collapsed,
  currentPath,
  onNavigate,
  isOrgRequired,
  getPath,
  badge
}: {
  path: string
  icon: any
  label: string
  collapsed: boolean
  currentPath?: string
  onNavigate?: (path: string) => void
  isOrgRequired: boolean
  getPath: (path: string) => string
  badge?: number
}) {
  return (
    <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className="group/menu-item relative">
      <Link
        to={getPath(path)}
        className={cn(
          "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left outline-hidden ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 text-sm",
          collapsed && "!size-8 !p-2",
          currentPath?.endsWith(`/${path}`) && "bg-sidebar-accent text-sidebar-accent-foreground",
          isOrgRequired && "opacity-50 cursor-not-allowed hover:bg-transparent"
        )}
        onClick={(e) => {
          if (isOrgRequired) {
            e.preventDefault()
            return
          }
          // Allow cmd+click or ctrl+click to work normally
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            onNavigate?.(getPath(path))
          }
        }}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className={cn(collapsed && "hidden", "flex-1")}>{label}</span>
        {badge !== undefined && badge > 0 && !collapsed && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-medium text-destructive-foreground">
                  {badge}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{badge} integration{badge > 1 ? 's' : ''} with credential issues</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Link>
    </li>
  )
}

export function Sidebar({ collapsed, onNavigate, currentPath }: SidebarProps) {
  const { user, logout } = useAuth()
  const { hasSettingsAccess, hasAdminAccess, hasRole } = useRoleAccess()
  const { orgSlug } = useParams()
  // Shared React Query hook: dedupes the fetch across both sidebar instances
  // (desktop + mobile drawer), refreshes every 10 min, and re-fetches on the
  // 'credentials-updated' event.
  const credentialErrorCount = useCredentialErrorCount(orgSlug)

  const getPath = (path: string) => {
    if (path.startsWith('/')) {
      // Absolute paths (like /organizations)
      return path
    } else if (orgSlug) {
      // Relative paths within organization
      return `/${orgSlug}/${path}`
    } else {
      // When no orgSlug (like on /organizations page), get the last active org
      const lastOrg = localStorage.getItem('activeOrganization')
      if (lastOrg) {
        return `/${lastOrg}/${path}`
      }
      // If no last org, these links shouldn't be clickable
      return '#'
    }
  }
  
  // Check if org-specific links should be disabled
  const isOrgRequired = !orgSlug && !localStorage.getItem('activeOrganization')
  
  return (
    <div data-sidebar="sidebar" className="flex h-full w-full flex-col">
      {/* Header */}
      <div data-slot="sidebar-header" data-sidebar="header" className="flex flex-col gap-2 p-2">
        <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
          <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className="group/menu-item relative">
            <a 
              href="#" 
              className={cn(
                "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left outline-hidden ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-8 text-sm",
                collapsed && "!p-1.5"
              )}
            >
              <img
                src="/logo_horizontal_big.png"
                alt="Glaium"
                className={cn("h-5 object-contain", collapsed ? "w-5" : "w-auto")}
              />
            </a>
          </li>
        </ul>
      </div>

      {/* Content */}
      <div 
        data-slot="sidebar-content" 
        data-sidebar="content" 
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-2 overflow-auto",
          collapsed && "overflow-hidden"
        )}
      >
        {/* Edge Group */}
        <div
          data-slot="sidebar-group"
          data-sidebar="group"
          className={cn(
            "relative flex w-full min-w-0 flex-col p-2",
            collapsed && "hidden"
          )}
        >
          <div
            data-slot="sidebar-group-label"
            data-sidebar="group-label"
            className="text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear"
          >
            Optimization
          </div>
          <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
            <OrgNavItem
              path="optimizer"
              icon={Target}
              label="Optimizer"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            <OrgNavItem
              path="agents"
              icon={Sparkles}
              label="Agents"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            {/* Hidden from the menu — routes still accessible directly:
            <OrgNavItem
              path="analysis"
              icon={MessageSquare}
              label="Analysis"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            <OrgNavItem
              path="scenarios"
              icon={GitCompareArrows}
              label="Scenarios"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            */}
          </ul>
        </div>

        {/* Cube Group */}
        <div 
          data-slot="sidebar-group" 
          data-sidebar="group" 
          className={cn(
            "relative flex w-full min-w-0 flex-col p-2",
            collapsed && "hidden"
          )}
        >
          <div 
            data-slot="sidebar-group-label" 
            data-sidebar="group-label" 
            className="text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear"
          >
            Cube
          </div>
          <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
            <OrgNavItem
              path="reports"
              icon={TrendingUp}
              label="Reports"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            <OrgNavItem
              path="normalizations"
              icon={GitBranch}
              label="Normalizations"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            <OrgNavItem
              path="integrations"
              icon={Plug}
              label="Integrations"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
              badge={credentialErrorCount}
            />
            <OrgNavItem
              path="model"
              icon={Layers}
              label="Model"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
          </ul>
        </div>

        {/* Ops Group */}
        <div
          data-slot="sidebar-group"
          data-sidebar="group"
          className={cn(
            "relative flex w-full min-w-0 flex-col p-2",
            collapsed && "hidden"
          )}
        >
          <div
            data-slot="sidebar-group-label"
            data-sidebar="group-label"
            className="text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear"
          >
            Ops
          </div>
          <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
            <OrgNavItem
              path="monitoring"
              icon={Activity}
              label="Monitoring"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />
            {hasRole('admin') && (
              <OrgNavItem
                path="health"
                icon={Heart}
                label="Health"
                collapsed={collapsed}
                currentPath={currentPath}
                onNavigate={onNavigate}
                isOrgRequired={isOrgRequired}
                getPath={getPath}
              />
            )}
          </ul>
        </div>

        {/* App Management (Guardian). The Apps SDK entry is hidden from the menu
            (its page still exists at /admin/apps-sdk); with Guardian as the only
            item, the group is Admin-only to match Guardian's backend gate. */}
        {hasAdminAccess() && <div
          data-slot="sidebar-group"
          data-sidebar="group"
          className={cn(
            "relative flex w-full min-w-0 flex-col p-2",
            collapsed && "hidden"
          )}
        >
          <div
            data-slot="sidebar-group-label"
            data-sidebar="group-label"
            className="text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear"
          >
            App Management
          </div>
          <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
            {hasAdminAccess() && <OrgNavItem
              path="guardian"
              icon={Shield}
              label="Guardian"
              collapsed={collapsed}
              currentPath={currentPath}
              onNavigate={onNavigate}
              isOrgRequired={isOrgRequired}
              getPath={getPath}
            />}
          </ul>
        </div>}

        {/* Bottom group */}
        <div data-slot="sidebar-group" data-sidebar="group" className="relative flex w-full min-w-0 flex-col p-2 mt-auto">
          <div data-slot="sidebar-group-content" data-sidebar="group-content" className="w-full text-sm">
            <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
              {/* Only show Settings link for users with manager, admin, or owner roles */}
              {hasSettingsAccess() && (
                <OrgNavItem 
                  path="settings" 
                  icon={Settings} 
                  label="Settings" 
                  collapsed={collapsed}
                  currentPath={currentPath}
                  onNavigate={onNavigate}
                  isOrgRequired={isOrgRequired}
                  getPath={getPath}
                />
              )}
              <OrgNavItem
                path="help"
                icon={HelpCircle}
                label="Help"
                collapsed={collapsed}
                currentPath={currentPath}
                onNavigate={onNavigate}
                isOrgRequired={isOrgRequired}
                getPath={getPath}
              />
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div 
        data-slot="sidebar-footer" 
        data-sidebar="footer" 
        className="flex flex-col gap-2 p-2"
      >
        <ul data-slot="sidebar-menu" data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-1">
          <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className="group/menu-item relative">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  data-slot="dropdown-menu-trigger" 
                  data-sidebar="menu-button" 
                  data-size="lg" 
                  data-active="false" 
                  className={cn(
                    "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left outline-hidden ring-sidebar-ring transition-[width,height,padding] focus-visible:ring-2",
                    "active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50",
                    "group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50",
                    "data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground",
                    "data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground",
                    "[&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
                    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground h-12 text-sm",
                    "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
                    collapsed && "!size-8 !p-0"
                  )}
                  type="button" 
                >
                  <span 
                    data-slot="avatar" 
                    className="relative flex size-8 shrink-0 overflow-hidden h-8 w-8 rounded-lg"
                  >
                    {user?.avatar ? (
                      <img 
                        data-slot="avatar-image" 
                        className="aspect-square size-full" 
                        alt={user?.name || 'User'} 
                        src={user.avatar}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-sm font-medium">
                        {user?.name ? user.name.charAt(0).toUpperCase() : user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                  </span>
                  <div className={cn("grid flex-1 text-left text-sm leading-tight", collapsed && "hidden")}>
                    <span className="truncate font-medium">{user?.name || 'Guest'}</span>
                    <span className="text-muted-foreground truncate text-xs">{user?.email || 'Not logged in'}</span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" alignOffset={8} className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg">
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <span className="relative flex size-8 shrink-0 overflow-hidden h-8 w-8 rounded-lg">
                      {user?.avatar ? (
                        <img 
                          className="aspect-square size-full" 
                          alt={user?.name || 'User'} 
                          src={user.avatar}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-sm font-medium">
                          {user?.name ? user.name.charAt(0).toUpperCase() : user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
                        </div>
                      )}
                    </span>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{user?.name || 'Guest'}</span>
                      <span className="text-muted-foreground truncate text-xs">{user?.email || 'Not logged in'}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => onNavigate?.(getPath('account'))}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    Account
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        </ul>
      </div>
    </div>
  )
}